import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Legend, ReferenceLine,
} from "recharts";
import { Loader2, ArrowLeft, TrendingUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BAND_COLORS = {
  V1: "#dc2626", V2: "#ea580c", V3: "#d97706", V4: "#ca8a04",
  V5: "#65a30d", V6: "#16a34a", V7: "#15803d",
};
const PILLAR_COLORS = { E: "#16a34a", S: "#d97706", G: "#4f46e5" };

export default function Trend() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/organizations/${id}/trend`);
        setData(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento trend");
      } finally { setLoading(false); }
    })();
  }, [id]);

  const ratingSeries = useMemo(() => {
    if (!data) return [];
    return data.series.map((s) => ({
      year: s.year,
      rating: s.final_rating != null ? Math.round(s.final_rating * 100) / 100 : null,
      band: s.band,
      profile: s.profile_level,
      status: s.status,
    }));
  }, [data]);

  const pillarSeries = useMemo(() => {
    if (!data) return [];
    return data.series.map((s) => ({
      year: s.year,
      E: s.score_e != null ? Math.round(s.score_e * 100) / 100 : null,
      S: s.score_s != null ? Math.round(s.score_s * 100) / 100 : null,
      G: s.score_g != null ? Math.round(s.score_g * 100) / 100 : null,
    }));
  }, [data]);

  // Topic breakdown: 21 topics × N years
  const topicGrid = useMemo(() => {
    if (!data || data.series.length === 0) return { topics: [], rows: [] };
    const allTopicCodes = new Set();
    data.series.forEach((s) => s.topics.forEach((t) => allTopicCodes.add(t.topic_code)));
    const topics = [...allTopicCodes].sort();
    const rows = topics.map((tc) => {
      const row = { topic_code: tc };
      let pillar = "";
      let label = tc;
      data.series.forEach((s) => {
        const t = s.topics.find((x) => x.topic_code === tc);
        if (t) {
          row[`y${s.year}`] = t.score != null ? Math.round(t.score * 100) / 100 : null;
          pillar = t.pillar;
          label = t.label;
        }
      });
      row.pillar = pillar;
      row.label = label;
      return row;
    });
    return { topics, rows };
  }, [data]);

  if (loading) {
    return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-viva-500" /></div>;
  }
  if (!data) return <div className="p-8">Trend non disponibile.</div>;

  const years = data.series.map((s) => s.year);
  const empty = data.count === 0;
  const single = data.count === 1;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="trend-page">
      <Button variant="ghost" onClick={() => navigate(-1)} data-testid="trend-back" className="mb-4 text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
      </Button>

      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-viva-600 mb-2 flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5" /> Trend pluriennale
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900" data-testid="trend-org-name">{data.organization_name}</h1>
        <p className="text-slate-500 mt-1">
          {empty
            ? "Nessun assessment registrato per questa azienda."
            : `${data.count} assessment · anni ${Math.min(...years)} – ${Math.max(...years)}`}
        </p>
      </div>

      {empty && (
        <Card className="p-12 rounded-2xl border-slate-100 shadow-soft text-center text-slate-500" data-testid="trend-empty">
          <TrendingUp className="w-10 h-10 mx-auto mb-3 text-slate-300" strokeWidth={1.25} />
          <p className="font-medium text-slate-700">Crea un primo assessment per iniziare il tracking</p>
        </Card>
      )}

      {single && (
        <Card className="p-4 mb-6 rounded-xl bg-amber-50 border-amber-200 text-amber-800 text-sm flex items-start gap-3" data-testid="trend-warning-single">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            Solo un assessment disponibile. Il trend pluriennale diventa significativo a partire dal secondo anno.
          </div>
        </Card>
      )}

      {!empty && (
        <>
          {/* Rating finale + Banda VIVA */}
          <Card className="p-6 rounded-2xl border-slate-100 shadow-soft mb-6" data-testid="trend-rating-card">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl font-bold text-slate-900">Rating finale</h2>
                <p className="text-xs text-slate-500">Score 0–100 e banda VIVA (V1–V7) per esercizio</p>
              </div>
              <div className="flex gap-2 flex-wrap justify-end">
                {ratingSeries.map((r) => (
                  <div key={r.year} className="px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-100">
                    <div className="text-[10px] uppercase text-slate-500">{r.year}</div>
                    <div className="font-heading font-bold text-slate-900 flex items-center gap-1.5">
                      {r.rating ?? "—"}
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white"
                        style={{ background: BAND_COLORS[r.band] || "#64748b" }}
                      >
                        {r.band || "—"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={ratingSeries} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="year" stroke="#64748b" fontSize={12} />
                  <YAxis domain={[0, 100]} stroke="#64748b" fontSize={12} />
                  <Tooltip
                    contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(v, _n, p) => [v, `Rating · ${p.payload.band}`]}
                  />
                  <ReferenceLine y={70} stroke="#16a34a" strokeDasharray="4 4" label={{ value: "V6", fontSize: 10, fill: "#16a34a", position: "right" }} />
                  <ReferenceLine y={50} stroke="#ca8a04" strokeDasharray="4 4" label={{ value: "V4", fontSize: 10, fill: "#ca8a04", position: "right" }} />
                  <Line
                    type="monotone" dataKey="rating" stroke="#15803d" strokeWidth={3}
                    dot={{ r: 5, strokeWidth: 2, fill: "#fff" }}
                    activeDot={{ r: 7 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Pillar scores E/S/G */}
          <Card className="p-6 rounded-2xl border-slate-100 shadow-soft mb-6" data-testid="trend-pillar-card">
            <div className="mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-900">Score per pilastro</h2>
              <p className="text-xs text-slate-500">Environmental, Social e Governance per esercizio</p>
            </div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={pillarSeries} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="year" stroke="#64748b" fontSize={12} />
                  <YAxis domain={[0, 100]} stroke="#64748b" fontSize={12} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="E" name="Environmental" stroke={PILLAR_COLORS.E} strokeWidth={2.5} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="S" name="Social" stroke={PILLAR_COLORS.S} strokeWidth={2.5} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="G" name="Governance" stroke={PILLAR_COLORS.G} strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Topic breakdown — heatmap-like grid */}
          <Card className="p-6 rounded-2xl border-slate-100 shadow-soft" data-testid="trend-topic-card">
            <div className="mb-4 flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <h2 className="font-heading text-xl font-bold text-slate-900">Breakdown per topic</h2>
                <p className="text-xs text-slate-500">21 topic ESG · score per esercizio (heatmap)</p>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-400 inline-block" /> &lt; 50</div>
                <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400 inline-block" /> 50–75</div>
                <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-viva-500 inline-block" /> ≥ 75</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="trend-topic-table">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-100">
                    <th className="px-3 py-2.5 w-12">Pil.</th>
                    <th className="px-3 py-2.5">Topic</th>
                    <th className="px-3 py-2.5">Etichetta</th>
                    {years.map((y) => (
                      <th key={y} className="px-3 py-2.5 text-center w-20">{y}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {topicGrid.rows.map((row) => (
                    <tr key={row.topic_code} data-testid={`trend-topic-row-${row.topic_code}`}>
                      <td className="px-3 py-2.5">
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
                          style={{ background: PILLAR_COLORS[row.pillar] || "#94a3b8" }}
                        >{row.pillar}</span>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-slate-700 font-medium">{row.topic_code}</td>
                      <td className="px-3 py-2.5 text-slate-700">{row.label}</td>
                      {years.map((y) => {
                        const v = row[`y${y}`];
                        return <td key={y} className="px-3 py-2.5 text-center"><ScoreCell v={v} /></td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function ScoreCell({ v }) {
  if (v == null) {
    return <span className="text-slate-300 text-xs">—</span>;
  }
  const bg = v >= 75 ? "bg-viva-100 text-viva-700"
    : v >= 50 ? "bg-amber-100 text-amber-700"
    : "bg-red-100 text-red-700";
  return (
    <span className={`inline-flex w-14 justify-center py-1 rounded-md text-xs font-semibold tabular-nums ${bg}`}>
      {Math.round(v)}
    </span>
  );
}
